import { Link } from 'react-router-dom';
import { ProductHeader } from '@/components/auth/ProductHeader';
import { ProductFooter } from '@/components/auth/ProductFooter';
import '@/modules/auth/auth.css';
import './legal.css';

export function TermsPage() {
  return (
    <div className="auth-page auth-page--with-chrome">
      <ProductHeader product="assess" />
      <div className="legal-page">
        <div className="legal-container">
          <nav className="legal-breadcrumb">
            <Link to="/login">← Back to Login</Link>
          </nav>
          <h1 className="legal-title">Terms of Service</h1>
          <p className="legal-updated">Last updated: 13 March 2026 · Effective: 13 March 2026</p>
          <p className="legal-updated" style={{ marginTop: '-0.5rem', marginBottom: '1.5rem' }}>
            Governed by Indian law · Courts of Chennai, Tamil Nadu
          </p>

          <div className="legal-highlight">
            By creating an account you agree to these Terms. Users under 18 require parental consent.
            We offer a 7-day refund on unused credits. Disputes are resolved by arbitration in Chennai.
          </div>

          <section className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using Paazio Assess ("Service"), you agree to be bound by these Terms of
              Service and our <Link to="/privacy">Privacy Policy</Link>. If using on behalf of an
              organisation (academy, school, institution), you confirm you have authority to bind that
              organisation. If you disagree, do not use the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Eligibility</h2>
            <ul>
              <li>You must be at least <strong>18 years old</strong>, or under 18 with verifiable parental or guardian consent, to use this Service.</li>
              <li>Under the DPDP Act 2023, verifiable parental consent is mandatory for all users under 18. Institutions enrolling minors must obtain this consent.</li>
              <li>You must be legally capable of entering contracts under Indian law.</li>
              <li>You must not be prohibited from using the Service under any applicable law.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Account Registration and Security</h2>
            <p>By registering, you agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Keep your credentials confidential — do not share your password or OTP codes</li>
              <li>Accept responsibility for all activity under your account</li>
              <li>Notify us immediately at <a href="mailto:support@paazio.com">support@paazio.com</a> of any unauthorised access</li>
              <li>Not create multiple accounts or use another person's account</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Services Description</h2>
            <p>Paazio Assess provides:</p>
            <ul>
              <li>Adaptive and non-adaptive online assessments (Computer Adaptive Testing)</li>
              <li>Section-based and templated assessments</li>
              <li>AI-generated question banks</li>
              <li>Subjective grading and automated scoring</li>
              <li>Proctoring with AI-based integrity monitoring</li>
              <li>Test links for public or invite-based assessments</li>
              <li>Certificates for successfully completed assessments</li>
              <li>LTI integration for learning management system embedding</li>
            </ul>
            <p>Feature availability depends on your plan and academy configuration. We may modify features with reasonable notice.</p>
          </section>

          <section className="legal-section">
            <h2>5. User Roles and Responsibilities</h2>
            <p><strong>Students:</strong> Take assessments honestly. Academic fraud (using another person's work, impersonation, circumventing proctoring) is a material breach of these Terms.</p>
            <p><strong>Organisers:</strong> Create and manage assessment content. Responsible for ensuring question accuracy and copyright compliance in uploaded content.</p>
            <p><strong>Academy Admins:</strong> Manage members, assessments, and results within their academy. Responsible for obtaining parental consent for minor students.</p>
            <p><strong>Graders:</strong> Grade subjective submissions fairly and promptly in accordance with the grading rubric provided.</p>
          </section>

          <section className="legal-section">
            <h2>6. Payment Terms</h2>
            <p>All prices are in Indian Rupees (INR) inclusive of applicable GST. Payments are processed by Razorpay (RBI-regulated). We do not store card or UPI credentials.</p>
            <ul>
              <li><strong>Assessment credits:</strong> Purchased and stored in your wallet; consumed per assessment attempt.</li>
              <li><strong>Subscription plans:</strong> Grant access to a defined number of assessments or platform features for a specified period.</li>
              <li><strong>Auto-renewal:</strong> You will be notified before any subscription auto-renews. Cancel anytime before renewal date.</li>
            </ul>
            <p>Pricing changes take effect with 14 days' advance notice and do not apply to current billing periods.</p>
          </section>

          <section className="legal-section">
            <h2>7. Refund and Cancellation Policy</h2>
            <p><strong>Assessment credits:</strong> Refundable within <strong>7 days</strong> of purchase if no assessment attempts have been made. Credits for started attempts are non-refundable.</p>
            <p><strong>Subscription plans:</strong> Full refund within 7 days if no assessments have been taken under the plan. Partial pro-rated refunds may be considered after the 7-day window.</p>
            <p>To request a refund, email <a href="mailto:support@paazio.com">support@paazio.com</a> with your order ID. Refunds are processed within 5–10 business days to the original payment method.</p>
            <p>This policy complies with the <strong>Consumer Protection Act 2019</strong> and <strong>Consumer Protection (E-Commerce) Rules 2020</strong>.</p>
          </section>

          <section className="legal-section">
            <h2>8. User Conduct and Prohibited Activities</h2>
            <p>You must not:</p>
            <ul>
              <li>Cheat, plagiarise, or use unauthorised assistance during assessments</li>
              <li>Attempt to circumvent or manipulate proctoring systems</li>
              <li>Share assessment questions or answers with others during or after an active assessment</li>
              <li>Impersonate any person or sit an assessment on someone else's behalf</li>
              <li>Upload content that infringes copyright or contains harmful material</li>
              <li>Attempt to access accounts or systems beyond your permissions</li>
              <li>Introduce malware or harmful code to the platform</li>
              <li>Violate any applicable Indian law including the IT Act 2000 and IPC</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Content and Intellectual Property</h2>
            <p><strong>Paazio IP:</strong> Platform software, design, brand, features, and AI question generation algorithms are owned by Paazio and protected under the Copyright Act 1957.</p>
            <p><strong>Organiser/Institution content:</strong> Questions, rubrics, and assessment materials remain the creator's IP, with a non-exclusive licence to Paazio to host and deliver them to enrolled students.</p>
            <p><strong>Certificates:</strong> Digital certificates issued by Paazio for completed assessments are authentic only through the platform verification system. Tampering with certificates is prohibited.</p>
          </section>

          <section className="legal-section">
            <h2>10. Proctoring and Assessment Integrity</h2>
            <ul>
              <li>You will be notified before any proctored assessment and must provide explicit consent to activate webcam monitoring.</li>
              <li>Proctoring captures periodic webcam snapshots and monitors for tab-switch events, copy-paste activity, and suspicious behaviour.</li>
              <li>Proctoring data is shared only with the administering institution's authorised staff and retained for 30 days.</li>
              <li>Any attempt to circumvent proctoring constitutes a material breach and may result in your assessment being invalidated and your account suspended.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>11. Limitation of Liability</h2>
            <p>To the maximum extent permitted by Indian law:</p>
            <ul>
              <li>Paazio's total liability shall not exceed amounts paid by you in the preceding 3 months.</li>
              <li>Paazio is not liable for indirect, consequential, or punitive damages.</li>
              <li>Paazio does not guarantee assessment result accuracy where errors arise from content provided by third-party institutions.</li>
              <li>Paazio is not liable for service interruptions outside our reasonable control.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>12. Governing Law and Dispute Resolution</h2>
            <p>These Terms are governed by the laws of the <strong>Republic of India</strong>.</p>
            <ul>
              <li><strong>Step 1:</strong> Informal negotiation — contact <a href="mailto:support@paazio.com">support@paazio.com</a> within 30 days.</li>
              <li><strong>Step 2:</strong> Mediation under the Mediation Act 2023.</li>
              <li><strong>Step 3:</strong> Binding arbitration under the Arbitration and Conciliation Act 1996, seat: <strong>Chennai, Tamil Nadu</strong>.</li>
            </ul>
            <p>Consumer disputes may be raised before the Consumer Disputes Redressal Forum under the Consumer Protection Act 2019.</p>
          </section>

          <section className="legal-section">
            <h2>13. Termination</h2>
            <p><strong>By you:</strong> Delete your account via settings or by emailing <a href="mailto:support@paazio.com">support@paazio.com</a>. Unused credits may be refunded per our refund policy.</p>
            <p><strong>By Paazio:</strong> We may suspend or terminate accounts for material breach of these Terms, academic fraud, security risks, or fraudulent activity. Payment obligations, IP rights, and dispute resolution provisions survive termination.</p>
          </section>

          <section className="legal-section">
            <h2>14. Changes to Terms</h2>
            <p>Material changes will be notified by email and in-platform notice at least 30 days before taking effect. Continued use after the effective date constitutes acceptance.</p>
          </section>

          <section className="legal-section">
            <h2>15. Contact</h2>
            <p>
              📧 <a href="mailto:support@paazio.com">support@paazio.com</a> (general support)<br />
              📧 <a href="mailto:legal@paazio.com">legal@paazio.com</a> (legal matters)<br />
              📞 <a href="tel:+919790398729">+91 97903 98729</a><br />
              Chennai, Tamil Nadu, India
            </p>
          </section>

          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', fontSize: '0.875rem', color: '#6b7280' }}>
            Related: <Link to="/privacy">Privacy Policy</Link>
          </div>
        </div>
      </div>
      <ProductFooter />
    </div>
  );
}
